import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ClosingSetupState, CLOSING_SETUP_FEATURE_KEY } from '../reducer/closing-setup.reducer';
import { IClosingSetup, IClosingSetupStep } from '../models/closing-setup.model';

/** The steps in their canonical order — the order the import banner renders. */
const STEP_ORDER: readonly IClosingSetupStep['key'][] = ['ERP_CONNECT', 'JOURNAL_SYNC', 'DOCUMENT_SYNC'];

/** A step that no longer blocks anything. A missing one counts as settled: there is nothing to wait for. */
function settled(step: IClosingSetupStep | undefined): boolean {
    return step === undefined || step.status === 'DONE' || step.status === 'SKIPPED';
}

export const selectClosingSetupState = createFeatureSelector<ClosingSetupState>(CLOSING_SETUP_FEATURE_KEY);

export const selectClosingSetup = createSelector(
    selectClosingSetupState,
    (state): IClosingSetup | null => state.setup,
);

export const selectClosingSetupStatus = createSelector(selectClosingSetupState, (state) => state.status);
export const selectClosingSetupError = createSelector(selectClosingSetupState, (state) => state.error);

/** Only the START refusal — never a poll tick. See `startError` in the reducer (R3-1). */
export const selectClosingStartError = createSelector(selectClosingSetupState, (state) => state.startError);
export const selectClosingStarting = createSelector(selectClosingSetupState, (state) => state.starting);
export const selectClosingSetupPolling = createSelector(selectClosingSetupState, (state) => state.polling);

// REV-22007 — the accounting file.
export const selectAccountingFileStatus = createSelector(
    selectClosingSetupState,
    (state) => state.accountingFileStatus,
);
export const selectAccountingFilePreview = createSelector(
    selectClosingSetupState,
    (state) => state.accountingFilePreview,
);
export const selectAccountingFileRefusal = createSelector(
    selectClosingSetupState,
    (state) => state.accountingFileRefusal,
);

/**
 * What the navigation gate should DO — the three answers the route actually has to tell apart.
 *
 * ⚠️ This replaced a `boolean | null` selector that collapsed three different facts into two values
 * plus a gap: "started", "not started", and "we could not ask". A guard that waits for a non-`null`
 * therefore waited FOREVER on a failed read — `canActivate` never emits, Angular neither navigates nor errors nor times out,
 * and the whole closing workspace becomes unreachable, including the tabs that have nothing to do
 * with the setup. That is exactly what happened (Code-Review Runde 1, B1).
 *
 * `unavailable` is deliberately its own answer and NOT folded into `pending`: a read that failed has
 * an answer, it just is not about the closing. The gate lets that case through — see the guard.
 */
export type ClosingGateDecision = 'pending' | 'started' | 'not-started' | 'unavailable';

export const selectClosingGate = createSelector(selectClosingSetupState, (state): ClosingGateDecision => {
    if (state.status === 'failed') {
        return 'unavailable';
    }
    if (state.status !== 'loaded') {
        return 'pending';
    }

    return (state.setup?.started ?? false) ? 'started' : 'not-started';
});

/**
 * The launch steps, always in order.
 *
 * Sorted here rather than trusted from the wire: the screen animates the first `RUNNING` row, and a
 * reordered array would move the animation to the wrong line.
 */
export const selectClosingSetupSteps = createSelector(
    selectClosingSetup,
    (setup): readonly IClosingSetupStep[] =>
        [...(setup?.steps ?? [])].sort((left, right) => STEP_ORDER.indexOf(left.key) - STEP_ORDER.indexOf(right.key)),
);

/**
 * Whether the pipeline has come to rest — the poll's stop condition.
 *
 * `finishedAt` and not "every step is terminal": a failed step stops the pipeline with the steps
 * behind it still `PENDING`, and a rule written over the steps would poll that closing for ever.
 */
export const selectClosingLaunchFinished = createSelector(
    selectClosingSetup,
    (setup): boolean => setup !== null && setup.finishedAt !== null,
);

/**
 * A started launch that has not come to rest — the import banner is up and the poll runs.
 *
 * The same `finishedAt` rule as {@link selectClosingLaunchFinished}, so the banner and the poll can
 * never disagree about whether the import still runs.
 */
export const selectClosingLaunchInFlight = createSelector(
    selectClosingSetup,
    (setup): boolean => setup !== null && setup.started && setup.finishedAt === null,
);

/**
 * Whether the accounting data is in. Until it is, the closing's steps are locked: every one of them
 * reads the accounts, and an empty page reads as a broken one.
 *
 * Open when there is no launch to wait for (never started, or a record without steps). A FAILED pull
 * keeps it locked: the data is not there either way.
 */
export const selectClosingAccountingReady = createSelector(selectClosingSetup, (setup): boolean => {
    if (setup === null || !setup.started) {
        return true;
    }

    return (
        settled(setup.steps.find((step) => step.key === 'ERP_CONNECT')) &&
        settled(setup.steps.find((step) => step.key === 'JOURNAL_SYNC'))
    );
});

/** The step that failed, if one did — the only row the screen puts an alarm on. */
export const selectClosingLaunchFailure = createSelector(
    selectClosingSetupSteps,
    (steps): IClosingSetupStep | null => steps.find((step) => step.status === 'FAILED') ?? null,
);
